import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery, gql } from '@apollo/client';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const CARD_LIMIT = 4;
const YEAR_LIMIT = 3;

interface SubActivity { title: string; }
interface ActivityItem { title: string; subactivities: SubActivity[]; }

const GET_ACTIVITIES = gql`
  query {
    activity {
      data {
        attributes {
          Activities {
            title
            subactivity(pagination: { page: 1, pageSize: 200 }) {
              title
            }
          }
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(8, 0),
    background: '#fff',
  },
  sectionTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 800,
    textAlign: 'center',
    textTransform: 'uppercase',
    color: '#062a51',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    textAlign: 'center',
    color: '#666',
    fontSize: '0.95rem',
    marginBottom: theme.spacing(1),
  },
  underline: {
    width: 50,
    height: 4,
    background: '#26a69a',
    borderRadius: 2,
    margin: '0 auto',
    marginBottom: theme.spacing(5),
  },
  tabRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
    marginBottom: theme.spacing(4),
    gap: theme.spacing(1),
  },
  tab: {
    padding: '9px 22px',
    borderRadius: 50,
    fontFamily: 'Karla, sans-serif',
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer',
    border: '2px solid #b2dfdb',
    background: '#fff',
    color: '#062a51',
    transition: 'all 0.25s ease',
    outline: 'none',
    '&:hover': {
      borderColor: '#26a69a',
      color: '#26a69a',
    },
  },
  tabActive: {
    background: '#26a69a !important',
    color: '#fff !important',
    borderColor: '#26a69a !important',
    boxShadow: '0 4px 12px rgba(38,166,154,0.35)',
  },
  // Flex-based centering so 2 cards sit in the middle, 4 fill the row
  cardsWrap: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    gap: 16,
    animation: '$fadeUp 0.35s ease both',
  },
  '@keyframes fadeUp': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to:   { opacity: 1, transform: 'translateY(0)' },
  },
  actCard: {
    width: 210,
    [theme.breakpoints.down('sm')]: { width: 160 },
    borderRadius: 14,
    padding: '24px 18px',
    cursor: 'pointer',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    background: '#f8fffe',
    border: '1px solid #e0f2f1',
    boxShadow: '0 2px 8px rgba(38,166,154,0.07)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    gap: theme.spacing(1.5),
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 24px rgba(38,166,154,0.16)',
      borderColor: '#26a69a',
    },
  },
  cardBubble: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: '#e0f7f5',
    color: '#26a69a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '1rem',
    flexShrink: 0,
  },
  cardTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 700,
    fontSize: '0.85rem',
    lineHeight: 1.45,
    color: '#062a51',
  },
  showMoreBtn: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  showMoreText: {
    color: '#26a69a',
    fontFamily: 'Karla, sans-serif',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    border: '1px dashed #26a69a',
    borderRadius: 50,
    padding: '6px 20px',
    background: 'transparent',
    outline: 'none',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '&:hover': {
      background: '#e0f7f5',
    },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
  },
}));

export default function ActivitiesHomeSection() {
  const classes = useStyles();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [gridKey, setGridKey] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const { loading } = useQuery(GET_ACTIVITIES, {
    onCompleted(data) {
      const raw = data?.activity?.data?.attributes?.Activities ?? [];
      const parsed: ActivityItem[] = raw.map((each: any) => ({
        title: each.title,
        subactivities: (each.subactivity ?? []).map((s: any) => ({ title: s.title })),
      }));
      setActivities(parsed);
    },
  });

  function handleTabClick(index: number) {
    if (index === activeTab) return;
    setActiveTab(index);
    setShowAll(false);
    setGridKey(prev => prev + 1);
  }

  // Only show the 3 most recent years
  const visibleYears = activities.slice(0, YEAR_LIMIT);
  const current = visibleYears[activeTab];
  const visibleCards = current
    ? showAll
      ? current.subactivities
      : current.subactivities.slice(0, CARD_LIMIT)
    : [];
  const hiddenCount = current ? current.subactivities.length - CARD_LIMIT : 0;

  return (
    <div className={classes.section}>
      <Container maxWidth="lg">

        <Typography variant="h4" className={classes.sectionTitle}>
          Activities
        </Typography>
        <Typography className={classes.subtitle}>
          Our work across the years — empowering communities in Kozhikode &amp; Malappuram
        </Typography>
        <div className={classes.underline} />

        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress style={{ color: '#26a69a' }} />
          </Box>
        ) : (
          <>
            {/* Year tabs — last 3 years only */}
            <div className={classes.tabRow}>
              {visibleYears.map((act, i) => (
                <button
                  key={i}
                  className={`${classes.tab} ${i === activeTab ? classes.tabActive : ''}`}
                  onClick={() => handleTabClick(i)}
                >
                  {act.title}
                </button>
              ))}
            </div>

            {/* Cards — centered, max 4, then show more */}
            {current && (
              <>
                <div key={gridKey} className={classes.cardsWrap}>
                  {visibleCards.map((sub, i) => (
                    <div
                      key={i}
                      className={classes.actCard}
                      onClick={() => { window.location.href = '/activities'; }}
                    >
                      <div className={classes.cardBubble}>{i + 1}</div>
                      <div className={classes.cardTitle}>{sub.title}</div>
                    </div>
                  ))}
                </div>

                {/* Show More / Show Less */}
                {hiddenCount > 0 && (
                  <div className={classes.showMoreBtn}>
                    <button
                      className={classes.showMoreText}
                      onClick={() => setShowAll(prev => !prev)}
                    >
                      {showAll
                        ? 'Show Less'
                        : `+${hiddenCount} more`}
                      <ExpandMoreIcon
                        style={{
                          fontSize: '1rem',
                          transition: 'transform 0.3s',
                          transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>
                  </div>
                )}
              </>
            )}

          </>
        )}
      </Container>
    </div>
  );
}
